import { AnimationCanvas } from '@suntex.core/animation';
import { Client } from '@suntex.fim/api-client';

import { VIFAddSub } from './createjs/VIFAddSub.js';
import { VIFGears } from './createjs/VIFGears.js';
import { VIFMulDiv } from './createjs/VIFMulDiv.js';
import { VIFRedoAddSub } from './createjs/VIFRedoAddSub.js';
import { VIFRulesAddSub } from './createjs/VIFRulesAddSub.js';
import { VIFRulesMulDiv } from './createjs/VIFRulesMulDiv.js';
import { VIFAddSub_prog } from './games/VIFAddSub';
import { VIFGears_prog } from './games/VIFGears';
import { VIFMulDiv_prog } from './games/VIFMulDiv';
import { VIFRedoAddSub_prog } from './games/VIFRedoAddSub';
import { VIFRulesAddSub_prog } from './games/VIFRulesAddSub';
import { VIFRulesMulDiv_prog } from './games/VIFRulesMulDiv';
import {
  type VifGameExportRoot,
  type VifGameParams,
  type VifGameProg,
  type VifOnComplete,
} from './games/types';

interface ClassicGame {
  /** Registers the Adobe-Animate composition onto the CreateJS/AdobeAn libs. */
  initGraphics: (cjs: typeof createjs, an: unknown) => void;
  /** The converted GameFrame program (run via `new prog(...).run()`). */
  prog: VifGameProg;
  compositionId: string;
}

/**
 * The seven VIF Adobe-Animate compositions. `compositionId`s are the keys Animate
 * assigned each composition (used by `AnimationCanvas` to look up the library).
 */
export const VIF_GAMES = {
  VIFAddSub: {
    initGraphics: VIFAddSub,
    prog: VIFAddSub_prog,
    compositionId: '38284E5C31E16E4C8959206435B0733D',
  },
  VIFMulDiv: {
    initGraphics: VIFMulDiv,
    prog: VIFMulDiv_prog,
    compositionId: 'D011E3B725D3BB4087B7DA993BA7A524',
  },
  VIFRedoAddSub: {
    initGraphics: VIFRedoAddSub,
    prog: VIFRedoAddSub_prog,
    compositionId: '35CF4A373E5DD84AADBA85687FE47CF4',
  },
  VIFRulesAddSub: {
    initGraphics: VIFRulesAddSub,
    prog: VIFRulesAddSub_prog,
    compositionId: '3D6A2DC00D1C524A8A9801BF8166275F',
  },
  VIFRulesMulDiv: {
    initGraphics: VIFRulesMulDiv,
    prog: VIFRulesMulDiv_prog,
    compositionId: 'F99A2D34C007BC4AB551A67EDC1614C2',
  },
  VIFGears: {
    initGraphics: VIFGears,
    prog: VIFGears_prog,
    compositionId: '93EDC93AE36724438917B68F3EA8A53E',
  },
} satisfies Record<string, ClassicGame>;

export type VifGameName = keyof typeof VIF_GAMES;

export interface VifGameViewProps {
  api: Client;
  /** Which VIF composition/program to run (e.g. the module's `filename`). */
  name: VifGameName;
  width: number;
  height: number;
  /** Typed initialization parameters (playerId, moduleId, stage, ...). */
  params: VifGameParams;
  /** Called by the game when the player finishes the final round. */
  onComplete: VifOnComplete;
}

export function VifGameView({
  api,
  name,
  width,
  height,
  params,
  onComplete,
}: VifGameViewProps) {
  const game = VIF_GAMES[name];

  return (
    <AnimationCanvas
      width={width}
      height={height}
      compositionId={game.compositionId}
      initGraphics={(cjs, an) => {
        game.initGraphics(cjs, an);
      }}
      onReady={({ lib, stage }) => {
        const exportRoot = new lib[name]!() as VifGameExportRoot;
        stage.addChild(exportRoot);
        createjs.Touch.enable(stage);
        game.prog({ api, stage, exportRoot, params, onComplete });
      }}
    />
  );
}
